/* eslint max-lines: ["error", { "max": 520, "skipBlankLines": true, "skipComments": true }] -- routing refactor planned; split deferred to that work */
import { AuthenticationStore } from "@istari-frontend/auth";
import { getEnv } from "@istari-frontend/shared";
import React, { useEffect } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { useAuth } from "react-oidc-context";
import { createBrowserRouter, Navigate, Outlet, ScrollRestoration, useLocation, useParams } from "react-router-dom";

import { AiChatFloatingWidget } from "~/ai-chat/feature/ai-chat-floating-widget";
import { AiChatProvider } from "~/ai-chat/feature/use-ai-chat";
import { useAiChatToggle } from "~/ai-chat/feature/use-ai-chat-toggle";

import App from "./App";
import { useIsChangeRequestsEnabled } from "./change-request/feature/change-request-config";
import ErrorBoundaryDisplay from "./components/ErrorBoundaryDisplay";
import { InfosecBanner } from "./components/infosec-banner";
import { ItarBanner } from "./components/itar-banner";
import { TopBannerOffsetEffect, useTopBannerOffset } from "./components/top-banner-offset";
import UserMembershipProvider from "./contexts/UserMembershipContext";
import { NotificationToastHost } from "./notifications/feature/notification-toast-host";
import { routes } from "./routes";
import { SystemEditGuard } from "./systems/feature/system-edit-guard";
import { createSystemHomeTabRoutes } from "./systems/feature/system-home-tab-routes";
import { useIsNewSystemsEnabled } from "./systems/use-is-new-systems-enabled";
import { UploadProvider } from "./upload/upload-provider";

const SystemHomeBranchesPage = React.lazy(() => import("./pages/Main/SystemHome/system-home-branches"));
const SystemHomeChangeRequestDetailPage = React.lazy(
  () => import("./pages/Main/SystemHome/system-home-change-request-detail"),
);
const SystemHomeChangeRequestsPage = React.lazy(() => import("./pages/Main/SystemHome/system-home-change-requests"));
const SystemHomeCommitsPage = React.lazy(() => import("./pages/Main/SystemHome/system-home-commits"));
const SystemHomeCreateChangeRequestPage = React.lazy(
  () => import("./pages/Main/SystemHome/system-home-create-change-request"),
);
const SystemHomeDirectoryPage = React.lazy(() => import("./pages/Main/SystemHome/system-home-directory"));
const SystemHomeIndexPage = React.lazy(() => import("./pages/Main/SystemHome/system-home-index"));
const SystemHomeWorkflowsPage = React.lazy(() => import("./pages/Main/SystemHome/system-home-workflows"));
const SystemHomePage = React.lazy(() => import("./pages/Main/SystemHome"));

const systemHomeTabElements = {
  index: <SystemHomeIndexPage />,
  directory: <SystemHomeDirectoryPage />,
  workflows: <SystemHomeWorkflowsPage />,
  branches: <SystemHomeBranchesPage />,
  commits: <SystemHomeCommitsPage />,
  changeRequests: <SystemHomeChangeRequestsPage />,
  changeRequestDetail: <SystemHomeChangeRequestDetailPage />,
  createChangeRequest: (
    <SystemEditGuard message="You need edit access to create a change request.">
      <SystemHomeCreateChangeRequestPage />
    </SystemEditGuard>
  ),
};

const systemHomeTabRoutes = createSystemHomeTabRoutes(systemHomeTabElements);

const SignIn = React.lazy(() => import("./pages/Authentication/SignIn"));

const AllSystems = React.lazy(() => import("./pages/Main/AllSystems"));
const SystemsDashboard = React.lazy(() => import("./pages/Main/SystemsDashboard"));

const AllModelsNew = React.lazy(() => import("./pages/Main/AllModelsNew/all-models-new"));
const Jobs = React.lazy(() => import("./pages/Main/Jobs"));
const Settings = React.lazy(() => import("./pages/Main/Settings"));

const Comment = React.lazy(() => import("./pages/Main/CommentPage"));
const Artifact = React.lazy(() => import("./pages/Main/Artifact"));
const SystemDetailsPage = React.lazy(() => import("./pages/Main/System"));
const FunctionAuthCallback = React.lazy(() => import("./pages/Main/FunctionAuthCallback"));

const AdminPageLayout = React.lazy(() => import("./pages/Main/Admin/admin-page-layout"));
const AdminUsersPage = React.lazy(() => import("./pages/Main/Admin/users-page"));
const AdminCompatibilityPage = React.lazy(() => import("./pages/Main/Admin/compatibility-page"));
const AdminControlTagsPage = React.lazy(() => import("./pages/Main/Admin/control-tags-page"));
const AdminInfosecPage = React.lazy(() => import("./pages/Main/Admin/infosec-page"));
const AdminIntegrationsPage = React.lazy(() => import("./pages/Main/Admin/integrations-page"));
const AdminAiSettingsPage = React.lazy(() => import("./pages/Main/Admin/ai-settings-page"));
const AdminAiModelsPage = React.lazy(() => import("./pages/Main/Admin/ai-models-page"));
const AdminConnectedSourcesPage = React.lazy(() => import("./pages/Main/Admin/connected-sources-page"));
const AdminUsagePage = React.lazy(() => import("./pages/Main/Admin/usage-page"));
const AdminSecureConnectionsPage = React.lazy(() => import("./pages/Main/Admin/secure-connections-page"));
const AdminSecureConnectionSyncStatusPage = React.lazy(
  () => import("./pages/Main/Admin/secure-connection-sync-status-page"),
);
const AgentManagementPage = React.lazy(() => import("./pages/Main/AgentManagement/agent-management-page"));
const CreateChangeRequestPage = React.lazy(() => import("./pages/Main/ChangeRequest/create-change-request-page"));
const ChangeRequestDetailPage = React.lazy(() => import("./pages/Main/ChangeRequest/change-request-detail-page"));
const NotificationsPage = React.lazy(() => import("./pages/Main/Notifications"));

const SearchResultsPage = React.lazy(() => import("./search/feature"));
const PageNotFound = React.lazy(() => import("./components/PageNotFound"));

const LineageDevPage = import.meta.env.DEV ? React.lazy(() => import("./pages/Dev/lineage-dev-page")) : null;

function StackedTopBanners() {
  const { infosecBannerOffset } = useTopBannerOffset();
  return (
    <>
      <TopBannerOffsetEffect />
      <InfosecBanner />
      <ItarBanner topOffset={infosecBannerOffset} />
    </>
  );
}

function UserMemPagesLayout() {
  const { isAiChatEnabled } = useAiChatToggle();

  const content = (
    <UserMembershipProvider>
      <StackedTopBanners />
      <NotificationToastHost />
      <UploadProvider>
        <Outlet />
      </UploadProvider>
    </UserMembershipProvider>
  );

  if (!isAiChatEnabled) {
    return content;
  }

  return (
    <AiChatProvider>
      {content}
      <AiChatFloatingWidget />
    </AiChatProvider>
  );
}

function AppWithErrorBoundary() {
  return (
    <ErrorBoundary FallbackComponent={ErrorBoundaryDisplay}>
      <App />
      {/* Keyed by location.key so independent history entries (incl. back/forward) restore their own scroll. */}
      <ScrollRestoration getKey={(location) => location.key} />
    </ErrorBoundary>
  );
}

function AuthManager() {
  const location = useLocation();
  const auth = useAuth();
  const AS = AuthenticationStore();
  const redirectPath = AS.consumeRedirectPath();

  return auth.isAuthenticated ? (
    <>{redirectPath ? <Navigate to={redirectPath} replace /> : <Outlet />}</>
  ) : (
    <Navigate to={routes.SignIn} state={{ from: location }} replace />
  );
}

function ChangeRequestGuard({ children }: { children: React.ReactNode }) {
  const isEnabled = useIsChangeRequestsEnabled();
  const { systemId } = useParams<{ systemId: string }>();
  if (!isEnabled) {
    return <Navigate to={systemId ? `/systems/${systemId}` : routes.Systems} replace />;
  }
  return <>{children}</>;
}

function SystemsDashboardGuard({ children }: { children: React.ReactNode }) {
  const isEnabled = useIsNewSystemsEnabled();
  if (!isEnabled) {
    return <Navigate to={routes.Systems} replace />;
  }
  return <>{children}</>;
}

const PageTitle = ({ title }: { title: string }) => {
  const location = useLocation();
  useEffect(() => {
    const baseTitle = "Istari Digital";
    document.title = title ? `${title} | ${baseTitle}` : baseTitle;
  }, [location.pathname, title]);
  return null;
};

export default PageTitle;

export const router = createBrowserRouter(
  [
    {
      path: routes.FunctionAuthCallback,
      element: (
        <>
          <PageTitle title="Function Authentication" />
          <FunctionAuthCallback />
        </>
      ),
    },
    {
      path: routes.Root,
      element: <AppWithErrorBoundary />,
      children: [
        {
          path: routes.SignIn,
          element: (
            <>
              <PageTitle title="Sign In" />
              <SignIn />
            </>
          ),
        },
        {
          element: <AuthManager />,
          children: [
            {
              element: <UserMemPagesLayout />,
              children: [
                {
                  path: "/",
                  element: <Navigate to={routes.Systems} />,
                },
                {
                  path: "/home",
                  element: <Navigate to={routes.Systems} replace />,
                },
                {
                  path: routes.Systems,
                  element: (
                    <>
                      <PageTitle title="Systems" />
                      <AllSystems />
                    </>
                  ),
                },
                {
                  path: routes.SystemsDashboard,
                  element: (
                    <SystemsDashboardGuard>
                      <PageTitle title="Systems" />
                      <SystemsDashboard />
                    </SystemsDashboardGuard>
                  ),
                },
                {
                  path: routes.SystemHome,
                  element: (
                    <SystemsDashboardGuard>
                      <PageTitle title="System" />
                      <SystemHomePage />
                    </SystemsDashboardGuard>
                  ),
                  children: [
                    ...systemHomeTabRoutes,
                    {
                      path: routes.SystemHomeBranchScope,
                      children: systemHomeTabRoutes,
                    },
                  ],
                },
                {
                  path: routes.Files,
                  element: (
                    <>
                      <PageTitle title="Files" />
                      <AllModelsNew />
                    </>
                  ),
                },
                {
                  path: `${routes.Files}/:modelId/:revisionId?`,
                  element: (
                    <>
                      <PageTitle title="Files" />
                      <AllModelsNew />
                    </>
                  ),
                },
                {
                  path: `${routes.Files}/:modelId/:revisionId?/artifact/:artifactId/:artifactRevisionId?`,
                  element: (
                    <>
                      <PageTitle title="Files" />
                      <AllModelsNew />
                    </>
                  ),
                },
                {
                  path: routes.FilesUnparentedArtifact,
                  element: (
                    <>
                      <PageTitle title="Files" />
                      <AllModelsNew />
                    </>
                  ),
                },
                {
                  path: routes.Jobs,
                  element: (
                    <>
                      <PageTitle title="Jobs" />
                      <Jobs />
                    </>
                  ),
                },
                {
                  path: routes.JobDetails,
                  element: (
                    <>
                      <PageTitle title="Job Details" />
                      <Jobs />
                    </>
                  ),
                },
                {
                  path: routes.Search,
                  element: (
                    <>
                      <PageTitle title="Search" />
                      <SearchResultsPage />
                    </>
                  ),
                },
                {
                  path: routes.Admin,
                  element: (
                    <>
                      <PageTitle title="Admin" />
                      <AdminPageLayout />
                    </>
                  ),
                  children: [
                    {
                      index: true,
                      element: <Navigate to={routes.AdminUsers} replace />,
                    },
                    {
                      path: routes.AdminUsers,
                      element: (
                        <>
                          <PageTitle title="Users Management" />
                          <AdminUsersPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminControlTags,
                      element: (
                        <>
                          <PageTitle title="Control Tags" />
                          <AdminControlTagsPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminInfosec,
                      element: (
                        <>
                          <PageTitle title="Infosec Level Management" />
                          <AdminInfosecPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminIntegrations,
                      element: (
                        <>
                          <PageTitle title="Integrations" />
                          <AdminIntegrationsPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminUsage,
                      element: (
                        <>
                          <PageTitle title="Usage Metrics" />
                          <AdminUsagePage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminCompatibility,
                      element: (
                        <>
                          <PageTitle title="Compatibility" />
                          <AdminCompatibilityPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminAiSettings,
                      element: (
                        <>
                          <PageTitle title="AI Steering" />
                          <AdminAiSettingsPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminAiModels,
                      element: (
                        <>
                          <PageTitle title="AI Models" />
                          <AdminAiModelsPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminConnectedSources,
                      element: (
                        <>
                          <PageTitle title="Connected Sources" />
                          <AdminConnectedSourcesPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminSecureConnections,
                      element: (
                        <>
                          <PageTitle title="Secure Connections" />
                          <AdminSecureConnectionsPage />
                        </>
                      ),
                    },
                    {
                      path: routes.AdminSecureConnectionSyncStatus,
                      element: (
                        <>
                          <PageTitle title="Sync Status" />
                          <AdminSecureConnectionSyncStatusPage />
                        </>
                      ),
                    },
                  ],
                },
                {
                  path: "/agent-management",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agents",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agent",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: "/admin/agent-management",
                  element: <Navigate to={routes.Agents} replace />,
                },
                {
                  path: routes.Agents,
                  element: (
                    <>
                      <PageTitle title="Agents" />
                      <AgentManagementPage />
                    </>
                  ),
                },
                {
                  path: routes.Notifications,
                  element: (
                    <>
                      <PageTitle title="Notifications" />
                      <NotificationsPage />
                    </>
                  ),
                },
                {
                  path: routes.Settings,
                  element: (
                    <>
                      <PageTitle title="Settings" />
                      <Settings />
                    </>
                  ),
                },
                {
                  path: routes.CreateChangeRequest,
                  element: (
                    <ChangeRequestGuard>
                      <SystemEditGuard message="You need edit access to create a change request.">
                        <PageTitle title="New Change Request" />
                        <CreateChangeRequestPage />
                      </SystemEditGuard>
                    </ChangeRequestGuard>
                  ),
                },
                {
                  path: routes.ChangeRequestDetail,
                  element: (
                    <ChangeRequestGuard>
                      <>
                        <PageTitle title="Change Request" />
                        <ChangeRequestDetailPage />
                      </>
                    </ChangeRequestGuard>
                  ),
                },
                {
                  path: routes.SystemDetails,
                  element: (
                    <>
                      <PageTitle title="System Details" />
                      <SystemDetailsPage />
                    </>
                  ),
                },
                {
                  path: routes.Artifact,
                  element: (
                    <>
                      <PageTitle title="Artifact" />
                      <Artifact />
                    </>
                  ),
                },
                {
                  path: routes.CommentDetails,
                  element: (
                    <>
                      <PageTitle title="Comment Details" />
                      <Comment />
                    </>
                  ),
                },
                ...(import.meta.env.DEV && LineageDevPage
                  ? [{ path: "/dev/lineage", element: <LineageDevPage /> }]
                  : []),
                {
                  path: routes.NotFound,
                  element: (
                    <>
                      <PageTitle title="Page Not Found" />
                      <PageNotFound />
                    </>
                  ),
                },
                {
                  path: "*",
                  element: (
                    <>
                      <PageTitle title="Page Not Found" />
                      <PageNotFound />
                    </>
                  ),
                },
              ],
            },
          ],
        },
      ],
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
    basename: getEnv("BASE_URL"),
  },
);
